import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu, X, Bell, MessageCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { BRAND } from "../../constants/brand";
import { Toaster } from "sonner";

export default function DashboardLayout({ items, title }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-950" data-testid="dashboard-layout">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white">
        <div className="flex h-16 items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3">
            <button className="rounded-md border border-neutral-200 p-2 md:hidden" onClick={() => setOpen((v) => !v)} data-testid="dashboard-menu-toggle">
              {open ? <X size={16} /> : <Menu size={16} />}
            </button>
            <Link to="/" className="flex items-center gap-2">
              <img src={BRAND.logo} alt="EarlyPrint" className="h-8 w-8 rounded-full" />
              <span className="font-display text-lg font-bold">Early<span className="text-lime-500">Print</span></span>
            </Link>
            <span className="ml-4 hidden text-xs font-semibold uppercase tracking-widest text-neutral-500 md:inline" data-testid="dashboard-title">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <a href={BRAND.social.whatsapp} target="_blank" rel="noopener noreferrer" className="hidden items-center gap-1 rounded-md border border-neutral-200 px-3 py-2 text-xs font-semibold hover:border-neutral-900 md:inline-flex" data-testid="dash-whatsapp">
              <MessageCircle size={14} /> WhatsApp
            </a>
            <button className="rounded-md border border-neutral-200 p-2 text-neutral-700 hover:border-neutral-900" aria-label="Notifications" data-testid="dash-bell"><Bell size={16} /></button>
            <div className="hidden items-center gap-2 rounded-md border border-neutral-200 px-3 py-1.5 md:flex" data-testid="dash-user">
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />
              ) : (
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-lime-400 text-xs font-bold">{(user?.name || user?.email || "U")[0]?.toUpperCase()}</div>
              )}
              <span className="max-w-[140px] truncate text-xs font-semibold">{user?.name || user?.email}</span>
            </div>
            <button onClick={doLogout} className="rounded-md border border-neutral-200 p-2 text-neutral-700 hover:border-red-400 hover:text-red-500" aria-label="Log out" data-testid="dash-logout"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed inset-y-16 left-0 z-20 w-60 border-r border-neutral-200 bg-white transition-transform md:sticky md:top-16 md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"} md:block`} data-testid="dashboard-sidebar">
          <nav className="p-3">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `mb-1 flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive ? "border-l-2 border-lime-500 bg-neutral-950 text-white" : "text-neutral-700 hover:bg-neutral-100"
                  }`
                }
                data-testid={`side-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <it.icon size={16} />
                <span>{it.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="min-h-[calc(100vh-4rem)] flex-1 p-4 md:p-6" data-testid="dashboard-main">
          <Outlet />
        </main>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
